import React, { useCallback, useEffect } from 'react'

import styled from 'styled-components'
import { useAppDispatch } from  "store"
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { PageSpiner, PhotoCard, PhotosGrid } from '@cloudike/web_ui_components'
import { usePreviewItemsUpdate } from 'features/photo/photo-preview/usePreviewItemsUpdate'
import { openPhotoPreviewThunk , PREVIEW_TYPES } from 'features/photo/photo-preview/photoPreviewSlice'
import { SDK_TYPES } from 'sdk/sdkConstants'
import { usePhotoSelection } from 'features/common/hooks/usePhotoSelection'
import classNames from 'classnames'

import { getTrashBinItemsSelector, getTrashBinSelectedItemsCountSelector, getTrashBinTotalItemsCountSelector, getTrashBinSelectedItemsIdsSelector } from './selectors'
import { loadMoreTrashBinItemsThunk, trashBinActions } from './trashBinSlice'
import { EmptyTrashBinPlaceholder } from './EmptyTrashBinPlaceholder'



interface TrashBinPhotosProps {
  className?: string,
  type: SDK_TYPES
}

export const TrashBinPhotos: React.FC<TrashBinPhotosProps> = ({ className = '', type }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const items = getTrashBinItemsSelector()
  const totalItemsCount = getTrashBinTotalItemsCountSelector()
  const selectedItemsCount = getTrashBinSelectedItemsCountSelector()
  const selectedItemsIds = getTrashBinSelectedItemsIdsSelector()

  const { ref: loaderBoxRef, inView: isLoaderInView } = useInView()

  usePreviewItemsUpdate(items, totalItemsCount)

  const loadMore = () => {
    dispatch(loadMoreTrashBinItemsThunk())
  }

  useEffect(() => {
    if (items.length && isLoaderInView) {
      loadMore()
    }
  }, [isLoaderInView, items])

  const handleItemSelection = useCallback((id) => {
    dispatch(trashBinActions.selectItem(id))
  }, [])

  const handleItemSelectionWithPressedShift = useCallback((id) => {
    dispatch(trashBinActions.selectItemWithPressedShift({ id }))
  }, [])

  const { handleMouseEnter, handleMouseLeave, mouseEnteredCardIdRef, isContainerScrollDisabled } = usePhotoSelection(handleItemSelection, handleItemSelectionWithPressedShift, !!selectedItemsIds.length)

  const handleOpenPreview = useCallback((id) => {
    dispatch(openPhotoPreviewThunk({
      items,
      totalItemsCount: totalItemsCount,
      currentItemId: id,
      sdkType: type,
      type: PREVIEW_TYPES.TRASH_BIN,
      loadMore
    }))
  }, [items, totalItemsCount])

  if (!items.length) {
    return (
      <TrashBinBox
        className={className}
      >
        <EmptyTrashBinPlaceholder
          texts={{
            title: t('l_common_noPhotosVideos'),
            description: t('l_common_trashPlaceholderPhotosDescription')
          }}
        />
      </TrashBinBox>
    )
  }

  return (
    <TrashBinBox
      className={className}
    >
      <InfoText>
        {t('l_common_trashbinMessage')}
      </InfoText>

      <AlbumItemsBox className={classNames({ 'with-overflow-hidden': isContainerScrollDisabled })}>
        <SPhotosGrid className={'grid-day'}>
          {items.map(item => (
            <PhotoCard
              key={item.id}
              id={item.id}
              imgUrl={(item._links as any)?.image_middle?.href}
              isSelected={selectedItemsIds.includes(item.id)}
              onSelect={(id) => mouseEnteredCardIdRef.current !== id && handleItemSelection(id)}
              onClick={handleOpenPreview}
              isCheckboxVisibleWithoutHover={selectedItemsCount > 0}
              type={item.type}
              onMouseEnter={(id) => !!selectedItemsIds.length && handleMouseEnter(id)}
              onMouseLeave={() => !!selectedItemsIds.length && handleMouseLeave()}
            />
          ))}
        </SPhotosGrid>

        {items.length < totalItemsCount && (
          <div ref={loaderBoxRef}>
            <StyledPageSpinner  />
          </div>
        )}
      </AlbumItemsBox>
    </TrashBinBox>
  )
}

const SPhotosGrid = styled(PhotosGrid)`
`

const TrashBinBox = styled.div`
  width: 100%;
  position: relative;
  height: 100%;
`

const AlbumItemsBox = styled.div`
  width: 100%;
  height: calc(100% - 157px);
  overflow: hidden scroll;
  padding-right: 20px;

  &::-webkit-scrollbar {
    width: 4px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--scroll-color);
    border-radius: 14px;

    :hover{
      cursor: pointer;
    }
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &.with-overflow-hidden {
    overflow: hidden;
  }
`

const StyledPageSpinner = styled(PageSpiner)`
  margin: 20px 0;
  height: 40px;
  width: 100%;
`

const InfoText = styled.p`
  margin-bottom: 16px;
  font-size: 14px;
  line-height: 130%;
  letter-spacing: -0.14px;
  color: var(--text-primary);
`
